// apps/web/src/lib/painel/pendencias.ts — Fase 17.8, Plano 13 (D-15; UI-SPEC U-11; T-17.8-13-03).
// Badges de pendência da sidebar: leitura de GET /api/v1/painel/pendencias e as regras puras do badge (rótulo até 99+,
// texto acessível no plural certo, quando pulsar). A autorização é do servidor: 403 = a sessão não vê nenhuma categoria,
// então não há badges (e o hook para de perguntar). Um erro de rede ou 5xx nunca quebra a navegação: o chamador mantém o
// último valor. Só UMA leitura em voo por vez (várias montagens, eventos e navegações compartilham a mesma promessa).
import type { PainelPendenciasResponse } from '@pieltec/contracts';

export const URL_PENDENCIAS = '/api/v1/painel/pendencias';

/** Teto exibido no badge: acima disso aparece "99+". */
export const TETO_BADGE = 99;

/** Sem badges (sessão sem nenhuma categoria visível ou resposta vazia). */
export const SEM_PENDENCIAS: PainelPendenciasResponse = { grupos: {}, itens: {} };

export type ResultadoPendencias =
  | { readonly tipo: 'sucesso'; readonly pendencias: PainelPendenciasResponse }
  /** 403: a sessão não pode ver nenhuma categoria; não há por que perguntar de novo. */
  | { readonly tipo: 'sem-acesso' }
  /** Erro de rede, 401, 5xx ou corpo ilegível: o chamador mantém o último valor, sem toast. */
  | { readonly tipo: 'erro' };

export type ContextoCarga = 'carga' | 'retorno-aba';

/** Aceita só `{ id: inteiro > 0 }`; qualquer outra coisa (inclusive o `{}` de um servidor sem a rota) vira vazio. */
function contagensValidas(bruto: unknown): Readonly<Record<string, number>> {
  const saida: Record<string, number> = {};
  if (typeof bruto !== 'object' || bruto === null || Array.isArray(bruto)) return saida;
  for (const [chave, valor] of Object.entries(bruto)) {
    if (typeof valor === 'number' && Number.isInteger(valor) && valor > 0) saida[chave] = valor;
  }
  return saida;
}

let emVoo: Promise<ResultadoPendencias> | null = null;

/**
 * Lê as pendências por módulo. Chamadas simultâneas compartilham a mesma leitura (dedupe por sessão).
 * `fetchImpl` existe para teste; a chamada real usa `fetch` com cookies.
 */
export function carregarPendencias(fetchImpl: typeof fetch = fetch): Promise<ResultadoPendencias> {
  if (emVoo !== null) return emVoo;
  const leitura = fetchImpl(URL_PENDENCIAS, { credentials: 'include' })
    .then(async (resposta): Promise<ResultadoPendencias> => {
      if (resposta.status === 403) return { tipo: 'sem-acesso' };
      if (!resposta.ok) return { tipo: 'erro' };
      const corpo = (await resposta.json()) as { grupos?: unknown; itens?: unknown } | null;
      return { tipo: 'sucesso', pendencias: { grupos: contagensValidas(corpo?.grupos), itens: contagensValidas(corpo?.itens) } };
    })
    .catch((): ResultadoPendencias => ({ tipo: 'erro' }))
    .finally(() => {
      emVoo = null;
    });
  emVoo = leitura;
  return leitura;
}

/** Texto do badge: nada com 0 (ou menos), o número até 99 e "99+" acima. */
export function rotuloBadge(contagem: number): string | null {
  if (!Number.isFinite(contagem) || contagem <= 0) return null;
  const inteiro = Math.floor(contagem);
  return inteiro > TETO_BADGE ? `${TETO_BADGE}+` : String(inteiro);
}

/** Texto para leitor de tela e tooltip: a contagem real (nunca "99+"), singular só para 1. */
export function rotuloAcessivel(contagem: number): string {
  return contagem === 1 ? '1 pendência' : `${contagem} pendências`;
}

/** Tooltip de um módulo: o nome e, com pendência, `, {n} pendência(s)`. */
export function rotuloComPendencia(rotulo: string, contagem: number): string {
  return contagem > 0 ? `${rotulo}, ${rotuloAcessivel(contagem)}` : rotulo;
}

/**
 * Quando o badge pulsa: só se a contagem mudou, nunca na primeira leitura (`anterior` indefinido) e nunca quando a carga
 * veio do retorno da aba ao primeiro plano.
 */
export function devePulsar(anterior: number | undefined, atual: number, contexto: ContextoCarga): boolean {
  if (anterior === undefined) return false;
  if (contexto === 'retorno-aba') return false;
  return anterior !== atual;
}

export function chaveGrupo(id: string): string {
  return `grupo:${id}`;
}

export function chaveItem(id: string): string {
  return `item:${id}`;
}

/**
 * Chaves (`grupo:{id}` e `item:{id}`) cujo badge deve pulsar entre duas leituras. `anterior` nulo = primeira leitura (ninguém
 * pulsa). Depois da primeira, um módulo ausente vale 0, então um badge que aparece ou some também conta como mudança.
 */
export function chavesQuePulsam(
  anterior: PainelPendenciasResponse | null,
  atual: PainelPendenciasResponse,
  contexto: ContextoCarga,
): ReadonlySet<string> {
  const pulsos = new Set<string>();
  if (anterior === null) return pulsos;
  const comparar = (antes: Readonly<Record<string, number>>, depois: Readonly<Record<string, number>>, chave: (id: string) => string): void => {
    for (const id of new Set([...Object.keys(antes), ...Object.keys(depois)])) {
      if (devePulsar(antes[id] ?? 0, depois[id] ?? 0, contexto)) pulsos.add(chave(id));
    }
  };
  comparar(anterior.grupos, atual.grupos, chaveGrupo);
  comparar(anterior.itens, atual.itens, chaveItem);
  return pulsos;
}

export function mesmasPendencias(a: PainelPendenciasResponse, b: PainelPendenciasResponse): boolean {
  const igual = (x: Readonly<Record<string, number>>, y: Readonly<Record<string, number>>): boolean => {
    const chavesX = Object.keys(x);
    return chavesX.length === Object.keys(y).length && chavesX.every((chave) => x[chave] === y[chave]);
  };
  return igual(a.grupos, b.grupos) && igual(a.itens, b.itens);
}
